const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/youtube-summarize`;

export const extractVideoId = (input: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = input.match(p);
    if (m) return m[1];
  }
  return null;
};

export interface StreamResult {
  summary: string;
  author?: string;
  model?: string;
  language?: string;
}

export async function streamSummary(
  url: string,
  onDelta: (text: string) => void,
  signal?: AbortSignal,
  onMeta?: (meta: { author?: string; model?: string; language?: string }) => void,
  preferredModel?: string,
  mode?: string
): Promise<StreamResult> {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ url, preferredModel, mode }),
    signal,
  });

  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.error || "Failed to summarize video");
  }
  if (!resp.body) throw new Error("No response body");

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullText = "";
  let author: string | undefined;
  let model: string | undefined;
  let language: string | undefined;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
      let line = buffer.slice(0, newlineIndex);
      buffer = buffer.slice(newlineIndex + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") break;
      try {
        const parsed = JSON.parse(jsonStr);
        // Check for metadata event (author info)
        if (parsed.metadata) {
          author = parsed.metadata.author || undefined;
          model = parsed.metadata.model || undefined;
          language = parsed.metadata.language || undefined;
          onMeta?.({ author, model, language });
          continue;
        }
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) {
          fullText += content;
          onDelta(fullText);
        }
      } catch {
        buffer = line + "\n" + buffer;
        break;
      }
    }
  }

  return { summary: fullText, author, model, language };
}
